import React, { useState, useEffect, useCallback } from 'react';
import { emailAPI, departmentsAPI, projectsAPI, whatsappAPI } from '../../../api/client';
import Modal from '../../Common/Modal';
import {
  PlusIcon, TrashIcon, ArrowPathIcon, LinkIcon,
  ClipboardDocumentIcon, CheckIcon, InformationCircleIcon,
  ChatBubbleLeftRightIcon, ExclamationTriangleIcon,
  ServerIcon, SignalIcon, BoltIcon, ClockIcon, FolderIcon, BellAlertIcon,
  CalendarDaysIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const INTERVAL_OPTIONS = [
  { value: 5,   label: '5 min' },
  { value: 30,  label: '30 min' },
  { value: 60,  label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 360, label: '6 hours' },
  { value: 720, label: '12 hours' },
];

// Live "next sync in …" countdown, ticking once a second.
function NextSyncCountdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return <span className="font-medium text-slate-500">due now…</span>;
  const totalSec = Math.floor(diff / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const text = h > 0 ? `in ${h}h ${m}m` : m > 0 ? `in ${m}m ${s}s` : `in ${s}s`;
  return <span className="font-medium text-slate-500">{text}</span>;
}

const PROVIDER_META = {
  gmail:   { label: 'Gmail',                icon: '📧', bg: 'bg-red-50'  },
  outlook: { label: 'Outlook / M365',       icon: '📨', bg: 'bg-blue-50' },
  imap:    { label: 'IMAP / Company Email', icon: '📬', bg: 'bg-green-50' },
};

const INITIAL_FORM = {
  name: '', provider: 'gmail', email_address: '',
  department_id: '', auto_process_cvs: false, cv_target_project_id: '',
  imap_host: '', imap_port: 993, imap_use_ssl: true,
  smtp_host: '', smtp_port: 465,
  imap_password: '',
};

export default function EmailSection() {
  const [configs, setConfigs]         = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects]       = useState([]);
  const [projectsByDept, setProjectsByDept] = useState({});  // {deptId: [projects]} for per-config target editing
  const [showModal, setShowModal]     = useState(false);
  const [syncing, setSyncing]         = useState({});
  const [testing, setTesting]         = useState({});
  const [savingSync, setSavingSync]   = useState({});
  const [form, setForm]               = useState(INITIAL_FORM);

  const [waSettings, setWaSettings]   = useState(null);
  const [waLoading, setWaLoading]     = useState(false);
  const [waError, setWaError]         = useState(null);
  const [waToggling, setWaToggling]   = useState(false);
  const [waCopied, setWaCopied]       = useState(false);

  const webhookUrl = `${API_URL}/api/whatsapp/incoming`;

  const loadData = useCallback(async () => {
    const [cfgRes, deptRes, projRes] = await Promise.all([
      emailAPI.listConfigs(),
      departmentsAPI.list(),
      projectsAPI.list(),            // all readable projects — grouped by dept below
    ]);
    setConfigs(cfgRes.data);
    setDepartments(deptRes.data);
    // Group projects by department so each config card can offer a target-project
    // selector for its own department.
    const grouped = {};
    for (const p of projRes.data) {
      (grouped[p.department_id] = grouped[p.department_id] || []).push(p);
    }
    setProjectsByDept(grouped);
  }, []);

  const loadWaSettings = useCallback(async () => {
    setWaLoading(true);
    setWaError(null);
    try {
      const res = await whatsappAPI.getSettings();
      setWaSettings(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404)             setWaError('restart');
      else if (status === 401 || status === 403) setWaError('auth');
      else                            setWaError('network');
    } finally {
      setWaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadWaSettings();
  }, [loadData, loadWaSettings]);

  const loadProjects = async (deptId) => {
    if (!deptId) return;
    const res = await projectsAPI.list(deptId);
    setProjects(res.data);
  };

  const createConfig = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        department_id: parseInt(form.department_id),
        cv_target_project_id: form.cv_target_project_id ? parseInt(form.cv_target_project_id) : null,
        imap_port: form.provider === 'imap' ? parseInt(form.imap_port) || 993 : undefined,
        smtp_port: form.provider === 'imap' ? parseInt(form.smtp_port) || 465 : undefined,
      };
      const res = await emailAPI.createConfig(payload);
      setConfigs((prev) => [...prev, res.data]);
      setShowModal(false);
      setForm(INITIAL_FORM);
      toast.success(
        form.provider === 'imap'
          ? 'IMAP account saved — click "Test" to verify'
          : 'Email config created'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create config');
    }
  };

  const connectEmail = async (config) => {
    if (config.provider === 'imap') return;
    try {
      const fn = config.provider === 'gmail' ? emailAPI.getGmailAuthUrl : emailAPI.getOutlookAuthUrl;
      const res = await fn(config.id);
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start OAuth flow.');
    }
  };

  const testImap = async (configId) => {
    setTesting((p) => ({ ...p, [configId]: true }));
    try {
      const res = await emailAPI.testImapConnection(configId);
      toast.success(res.data.message || 'IMAP connection successful');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'IMAP connection test failed');
    } finally {
      setTesting((p) => ({ ...p, [configId]: false }));
    }
  };

  const syncEmails = async (configId) => {
    setSyncing((p) => ({ ...p, [configId]: true }));
    try {
      const res = await emailAPI.syncEmails(configId);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing((p) => ({ ...p, [configId]: false }));
    }
  };

  const deleteConfig = async (id) => {
    if (!window.confirm('Delete this email config?')) return;
    await emailAPI.deleteConfig(id);
    setConfigs((p) => p.filter((c) => c.id !== id));
    toast.success('Deleted');
  };

  const updateCfg = async (cfg, payload, successMsg) => {
    setSavingSync((s) => ({ ...s, [cfg.id]: true }));
    try {
      const res = await emailAPI.updateConfig(cfg.id, payload);
      setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? res.data : c)));
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update auto-sync');
    } finally {
      setSavingSync((s) => ({ ...s, [cfg.id]: false }));
    }
  };

  const toggleAutoSync = (cfg) =>
    updateCfg(cfg, { auto_sync_enabled: !cfg.auto_sync_enabled },
      cfg.auto_sync_enabled ? 'Auto-sync disabled' : 'Auto-sync enabled');

  const changeInterval = (cfg, val) =>
    updateCfg(cfg, { auto_sync_interval_minutes: parseInt(val) }, 'Auto-sync interval updated');

  const changeTargetProject = (cfg, val) =>
    updateCfg(cfg, { cv_target_project_id: val ? parseInt(val) : null }, 'Target project updated');

  const changeHrEmail = (cfg, val) =>
    updateCfg(cfg, { hr_notification_email: val.trim() || null }, 'HR notification email updated');

  const toggleCalendarImport = (cfg) =>
    updateCfg(cfg, { calendar_import_enabled: !cfg.calendar_import_enabled },
      cfg.calendar_import_enabled ? 'Calendar import disabled' : 'Calendar import enabled');

  const toggleDraftReply = (cfg) =>
    updateCfg(cfg, { draft_reply_enabled: !cfg.draft_reply_enabled },
      cfg.draft_reply_enabled ? 'AI draft replies disabled' : 'AI draft replies enabled');

  const changeDraftSignature = (cfg, val) =>
    updateCfg(cfg, { draft_signature: val.trim() || null }, 'Draft signature updated');

  const changeDraftSkipSenders = (cfg, val) =>
    updateCfg(cfg, { draft_skip_senders: val.trim() || null }, 'Skip senders updated');

  const changeDraftSkipSubjects = (cfg, val) =>
    updateCfg(cfg, { draft_skip_subjects: val.trim() || null }, 'Skip subjects updated');

  const changeDraftSignatureImage = (cfg, dataUrl) =>
    updateCfg(cfg, { draft_signature_image: dataUrl || null },
      dataUrl ? 'Signature image updated' : 'Signature image removed');

  const onSignatureImageFile = (cfg, file) => {
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error('Image too large — max 512 KB'); return; }
    const reader = new FileReader();
    reader.onload = () => changeDraftSignatureImage(cfg, reader.result);
    reader.readAsDataURL(file);   // → data:image/…;base64,…
  };

  const deptName = (id) => departments.find((d) => d.id === id)?.name || '—';

  const toggleWhatsApp = async (enabled) => {
    setWaToggling(true);
    try {
      const res = await whatsappAPI.updateSettings({ enabled });
      setWaSettings((prev) => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? 'WhatsApp intake enabled' : 'WhatsApp intake disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update setting');
    } finally {
      setWaToggling(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setWaCopied(true);
      setTimeout(() => setWaCopied(false), 2000);
      toast.success('Webhook URL copied!');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const WaErrorBanner = () => {
    const messages = {
      restart: { text: 'WhatsApp settings endpoint not found — the backend needs to be restarted.', action: 'Retry after restart' },
      auth:    { text: 'Could not authenticate. Please refresh the page.', action: 'Retry' },
      network: { text: 'Could not reach the backend. Check that the server is running.', action: 'Retry' },
    };
    const msg = messages[waError] || messages.network;
    return (
      <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-800">{msg.text}</p>
        </div>
        <button
          onClick={loadWaSettings}
          disabled={waLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 flex-shrink-0"
        >
          <ArrowPathIcon className={`w-3.5 h-3.5 ${waLoading ? 'animate-spin' : ''}`} />
          {waLoading ? 'Checking…' : msg.action}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Email accounts */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Email Accounts</h3>
            <p className="text-xs text-slate-500">Connect Gmail, Outlook, or company IMAP for CV processing and notifications</p>
          </div>
          <button
            onClick={() => { setForm(INITIAL_FORM); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" /> Add Email Account
          </button>
        </div>

        {configs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400">
            <p className="text-4xl mb-3">📧</p>
            <p className="font-medium">No email accounts connected</p>
            <p className="text-sm mt-1">Add a Gmail, Outlook, or company IMAP account</p>
          </div>
        ) : (
          <div className="space-y-4">
            {configs.map((cfg) => {
              const meta = PROVIDER_META[cfg.provider] || PROVIDER_META.gmail;
              return (
                <div key={cfg.id} className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${meta.bg}`}>
                        {meta.icon}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{cfg.name}</p>
                        <p className="text-sm text-slate-500">{cfg.email_address}</p>
                        {cfg.provider === 'imap' && cfg.imap_host && (
                          <p className="text-xs text-slate-400 font-mono mt-0.5">
                            {cfg.imap_host}:{cfg.imap_port || 993} → SMTP {cfg.smtp_host || cfg.imap_host}:{cfg.smtp_port || 465}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            cfg.is_connected ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {cfg.is_connected ? '● Connected' : '○ Not connected'}
                          </span>
                          <span className="text-xs text-slate-400">{meta.label}</span>
                          <span className="text-xs text-slate-400">{deptName(cfg.department_id)}</span>
                          {cfg.auto_process_cvs && !cfg.calendar_import_enabled && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                              CV Auto-processing ON
                            </span>
                          )}
                          {cfg.calendar_import_enabled && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-700">
                              Calendar Import ON
                            </span>
                          )}
                          {cfg.draft_reply_enabled && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-700">
                              AI Drafts ON
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {cfg.provider === 'imap' ? (
                        <>
                          <button
                            onClick={() => testImap(cfg.id)}
                            disabled={testing[cfg.id]}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                          >
                            <SignalIcon className={`w-4 h-4 ${testing[cfg.id] ? 'animate-pulse' : ''}`} />
                            {testing[cfg.id] ? 'Testing…' : 'Test'}
                          </button>
                          {cfg.is_connected && (cfg.auto_process_cvs || cfg.calendar_import_enabled || cfg.draft_reply_enabled) && (
                            <button
                              onClick={() => syncEmails(cfg.id)}
                              disabled={syncing[cfg.id]}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                            >
                              <ArrowPathIcon className={`w-4 h-4 ${syncing[cfg.id] ? 'animate-spin' : ''}`} />
                              {syncing[cfg.id] ? 'Syncing…' : 'Sync Now'}
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {!cfg.is_connected && (
                            <button
                              onClick={() => connectEmail(cfg)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                            >
                              <LinkIcon className="w-4 h-4" /> Connect
                            </button>
                          )}
                          {cfg.is_connected && (cfg.auto_process_cvs || cfg.calendar_import_enabled || cfg.draft_reply_enabled) && (
                            <button
                              onClick={() => syncEmails(cfg.id)}
                              disabled={syncing[cfg.id]}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                            >
                              <ArrowPathIcon className={`w-4 h-4 ${syncing[cfg.id] ? 'animate-spin' : ''}`} />
                              {syncing[cfg.id] ? 'Syncing…' : 'Sync Now'}
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => deleteConfig(cfg.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Auto-sync controls (only when the account can actually sync) */}
                  {cfg.is_connected && (cfg.auto_process_cvs || cfg.calendar_import_enabled || cfg.draft_reply_enabled) ? (
                    <div className="mt-3 ml-[52px] flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-2">
                        <BoltIcon className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-medium text-slate-600">Auto Sync</span>
                        <button
                          onClick={() => toggleAutoSync(cfg)}
                          disabled={savingSync[cfg.id]}
                          title={cfg.auto_sync_enabled ? 'Disable auto-sync' : 'Enable auto-sync'}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                            cfg.auto_sync_enabled ? 'bg-primary-500' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                            cfg.auto_sync_enabled ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>

                      {cfg.auto_sync_enabled && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400">every</span>
                          <select
                            value={cfg.auto_sync_interval_minutes ?? 30}
                            onChange={(e) => changeInterval(cfg, e.target.value)}
                            disabled={savingSync[cfg.id]}
                            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                          >
                            {INTERVAL_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {cfg.auto_sync_enabled && cfg.next_sync && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <ClockIcon className="w-3.5 h-3.5" /> Next sync <NextSyncCountdown target={cfg.next_sync} />
                        </span>
                      )}

                      {cfg.last_synced && (
                        <span className="text-xs text-slate-400">
                          Last synced: {new Date(cfg.last_synced).toLocaleString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    cfg.last_synced && (
                      <p className="text-xs text-slate-400 mt-2 ml-[52px]">
                        Last synced: {new Date(cfg.last_synced).toLocaleString()}
                      </p>
                    )
                  )}

                  {/* Target project — editable whenever CV auto-processing is on,
                      regardless of connection status. Hidden when calendar import
                      is on (the two modes are mutually exclusive). */}
                  {cfg.auto_process_cvs && !cfg.calendar_import_enabled && (
                    <div className="mt-3 ml-[52px] flex items-center gap-2 border-t border-slate-100 pt-3">
                      <FolderIcon className="w-4 h-4 text-slate-400" />
                      <span className="text-xs font-medium text-slate-600">Target Project</span>
                      <select
                        value={cfg.cv_target_project_id ?? ''}
                        onChange={(e) => changeTargetProject(cfg, e.target.value)}
                        disabled={savingSync[cfg.id]}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                      >
                        <option value="">— No target project —</option>
                        {(projectsByDept[cfg.department_id] || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Email → Calendar auto-import. Shown only when connected.
                      Turning it on replaces CV screening for this account. */}
                  {cfg.is_connected && (
                    <div className="mt-3 ml-[52px] border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-slate-600">Auto-import to Calendar</span>
                        <button
                          onClick={() => toggleCalendarImport(cfg)}
                          disabled={savingSync[cfg.id]}
                          title={cfg.calendar_import_enabled ? 'Disable calendar import' : 'Enable calendar import'}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                            cfg.calendar_import_enabled ? 'bg-primary-500' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                            cfg.calendar_import_enabled ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 ml-6">
                        Scans this inbox for meeting/event emails and creates Google Calendar events with reminders. Replaces CV screening for this account.
                      </p>
                    </div>
                  )}

                  {/* AI draft replies — generates (never sends) reply drafts to
                      the account's Drafts folder. IMAP accounts only. */}
                  {cfg.is_connected && cfg.provider === 'imap' && (
                    <div className="mt-3 ml-[52px] border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-2">
                        <PencilSquareIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-slate-600">AI Draft Replies</span>
                        <button
                          onClick={() => toggleDraftReply(cfg)}
                          disabled={savingSync[cfg.id]}
                          title={cfg.draft_reply_enabled ? 'Disable AI draft replies' : 'Enable AI draft replies'}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                            cfg.draft_reply_enabled ? 'bg-primary-500' : 'bg-slate-300'
                          }`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                            cfg.draft_reply_enabled ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 ml-6">
                        AI generates draft replies for incoming emails. Review and send manually from your inbox.
                      </p>

                      {/* Draft signature (HTML + embedded image) — only when drafts are on */}
                      {cfg.draft_reply_enabled && (
                        <div className="mt-3 ml-6 space-y-2">
                          <div>
                            <label className="text-xs font-medium text-slate-600">Skip Senders</label>
                            <input
                              type="text"
                              defaultValue={cfg.draft_skip_senders || ''}
                              onBlur={(e) => {
                                if (e.target.value.trim() !== (cfg.draft_skip_senders || '')) {
                                  changeDraftSkipSenders(cfg, e.target.value);
                                }
                              }}
                              disabled={savingSync[cfg.id]}
                              placeholder="e.g. noreply@, information@unclk.com, hr@"
                              className="mt-1 w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                            />
                            <p className="text-xs text-slate-400 mt-0.5">Emails from these senders will not be drafted.</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Skip Subjects (keywords)</label>
                            <input
                              type="text"
                              defaultValue={cfg.draft_skip_subjects || ''}
                              onBlur={(e) => {
                                if (e.target.value.trim() !== (cfg.draft_skip_subjects || '')) {
                                  changeDraftSkipSubjects(cfg, e.target.value);
                                }
                              }}
                              disabled={savingSync[cfg.id]}
                              placeholder="e.g. leave list, salary, newsletter, statement"
                              className="mt-1 w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                            />
                            <p className="text-xs text-slate-400 mt-0.5">Emails with these keywords in the subject will be skipped.</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600">Draft Signature HTML</label>
                            <textarea
                              defaultValue={cfg.draft_signature || ''}
                              onBlur={(e) => {
                                if (e.target.value.trim() !== (cfg.draft_signature || '')) {
                                  changeDraftSignature(cfg, e.target.value);
                                }
                              }}
                              disabled={savingSync[cfg.id]}
                              rows={3}
                              placeholder={'<b>Dr. Sanjayadeve De Silva Munasinghe</b><br>Chairman, UN Consortium'}
                              className="mt-1 w-full text-xs font-mono border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                            />
                            <p className="text-xs text-slate-400 mt-0.5">
                              Appended to AI draft replies (HTML). The uploaded image is embedded below it via cid.
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {cfg.draft_signature_image ? (
                              <img src={cfg.draft_signature_image} alt="signature"
                                className="h-10 rounded border border-slate-200 bg-white" />
                            ) : (
                              <span className="text-xs text-slate-400">No signature image</span>
                            )}
                            <label className="text-xs px-2 py-1 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                              {cfg.draft_signature_image ? 'Replace image' : 'Upload image'}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => { onSignatureImageFile(cfg, e.target.files?.[0]); e.target.value = ''; }}
                              />
                            </label>
                            {cfg.draft_signature_image && (
                              <button
                                onClick={() => changeDraftSignatureImage(cfg, null)}
                                disabled={savingSync[cfg.id]}
                                className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* HR notification email — alerts when a candidate books an
                      interview slot. Independent of CV auto-processing. */}
                  <div className="mt-3 ml-[52px] border-t border-slate-100 pt-3">
                    <div className="flex items-center gap-2">
                      <BellAlertIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-slate-600 flex-shrink-0">HR Notification Email</span>
                      <input
                        type="text"
                        defaultValue={cfg.hr_notification_email || ''}
                        onBlur={(e) => {
                          if (e.target.value.trim() !== (cfg.hr_notification_email || '')) {
                            changeHrEmail(cfg, e.target.value);
                          }
                        }}
                        disabled={savingSync[cfg.id]}
                        placeholder="hr@example.com, manager@example.com"
                        className="flex-1 min-w-[12rem] text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1 ml-6">
                      Receives alerts when candidates book interviews. Separate multiple emails with commas.
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* WhatsApp */}
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">WhatsApp CV Intake</h3>
            <p className="text-xs text-slate-500">Receive CVs via WhatsApp using Twilio Sandbox</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {waError && <div className="p-4 border-b border-slate-100"><WaErrorBanner /></div>}

          <div className="p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
              >
                <span className="text-white font-extrabold text-sm">WA</span>
              </div>
              <div>
                <p className="font-semibold text-slate-800">WhatsApp CV Intake</p>
                {waLoading && !waSettings ? (
                  <div className="h-4 w-44 bg-slate-100 rounded animate-pulse mt-1" />
                ) : (
                  <p className="text-sm text-slate-500 font-mono mt-0.5">
                    {waSettings?.whatsapp_number || 'whatsapp:+14155238886'}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  {waLoading && !waSettings ? (
                    <div className="h-5 w-24 bg-slate-100 rounded-full animate-pulse" />
                  ) : (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      waSettings?.is_configured ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {waSettings?.is_configured ? '● Configured' : '○ Not configured'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-sm text-slate-600 font-medium select-none">
                {waSettings?.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                onClick={() => { if (waSettings) toggleWhatsApp(!waSettings.enabled); }}
                disabled={waToggling || waLoading || !waSettings}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                  waSettings?.enabled ? 'bg-green-500' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  waSettings?.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          <div className="border-t border-slate-100" />

          <div className="p-5 space-y-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                Webhook URL — paste into Twilio Console
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 font-mono truncate select-all">
                  {webhookUrl}
                </code>
                <button
                  onClick={copyWebhook}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 flex-shrink-0"
                >
                  {waCopied
                    ? <><CheckIcon className="w-3.5 h-3.5 text-green-600" />Copied</>
                    : <><ClipboardDocumentIcon className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
            </div>

            <div className="flex gap-3 p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #bbf7d0' }}>
              <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
              <div className="space-y-1.5 text-sm" style={{ color: '#15803d' }}>
                <p className="font-semibold">How it works</p>
                <ul className="space-y-1 text-xs" style={{ color: '#166534' }}>
                  <li className="flex items-start gap-1.5"><span className="font-bold mt-0.5">→</span> Candidates send their CV as a PDF to this WhatsApp number.</li>
                  <li className="flex items-start gap-1.5"><span className="font-bold mt-0.5">→</span> The system automatically downloads, saves, and AI-screens the CV.</li>
                  <li className="flex items-start gap-1.5"><span className="font-bold mt-0.5">→</span> The candidate appears in <strong>HR → CV Screening</strong> with a score and matched position.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add email account modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Email Account">
        <form onSubmit={createConfig} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Display Name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. HR Company Mail"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Provider *</label>
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="gmail">Gmail</option>
                <option value="outlook">Outlook / Microsoft 365</option>
                <option value="imap">IMAP / Company Email (Hostinger, cPanel…)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
            <input
              required type="email"
              value={form.email_address}
              onChange={(e) => setForm({ ...form, email_address: e.target.value })}
              placeholder="hr@yourcompany.com"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          {form.provider === 'imap' && (
            <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <ServerIcon className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">IMAP / SMTP Connection</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">IMAP Host *</label>
                  <input
                    required
                    value={form.imap_host}
                    onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                    placeholder="mail.yourcompany.com"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">IMAP Port</label>
                  <input
                    type="number"
                    value={form.imap_port}
                    onChange={(e) => setForm({ ...form, imap_port: e.target.value })}
                    placeholder="993"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer w-fit">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, imap_use_ssl: !form.imap_use_ssl })}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    form.imap_use_ssl ? 'bg-primary-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    form.imap_use_ssl ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
                <span className="text-sm text-slate-700">
                  Use SSL <span className="text-xs text-slate-400">(recommended — port 993)</span>
                </span>
              </label>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    SMTP Host <span className="text-slate-400">(for sending replies)</span>
                  </label>
                  <input
                    value={form.smtp_host}
                    onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                    placeholder="Same as IMAP host if blank"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">SMTP Port</label>
                  <input
                    type="number"
                    value={form.smtp_port}
                    onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                    placeholder="465"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Password *</label>
                <input
                  required
                  type="password"
                  value={form.imap_password}
                  onChange={(e) => setForm({ ...form, imap_password: e.target.value })}
                  placeholder="Email account password"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  autoComplete="new-password"
                />
              </div>

              <p className="text-xs text-slate-400">
                For Hostinger / cPanel: IMAP host is usually <code className="font-mono">mail.yourdomain.com</code>, port 993 SSL.
                SMTP is the same host on port 465.
              </p>
            </div>
          )}

          {form.provider !== 'imap' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                After saving, click <strong>Connect</strong> on the card to complete OAuth authorisation.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Department *</label>
            <select
              required
              value={form.department_id}
              onChange={(e) => {
                setForm({ ...form, department_id: e.target.value, cv_target_project_id: '' });
                loadProjects(e.target.value);
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">Select department…</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-3 py-1">
            <input
              type="checkbox"
              id="auto_cv"
              checked={form.auto_process_cvs}
              onChange={(e) => setForm({ ...form, auto_process_cvs: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="auto_cv" className="text-sm font-medium text-slate-700">
              Auto-process CV emails from this account
            </label>
          </div>

          {form.auto_process_cvs && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Save CVs to Project</label>
              <select
                value={form.cv_target_project_id}
                onChange={(e) => setForm({ ...form, cv_target_project_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="">Select project…</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              {form.provider === 'imap' ? 'Save Account' : 'Add & Connect Later'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
